import { createSelector } from 'reselect'
import { handle } from 'redux-pack'
import AwesomeDebouncePromise from 'awesome-debounce-promise'

import { isDomainAvailable } from '../../../remote/organizations'
import { getEmailHandler } from '../../../remote/email'
import { cancelSignupReminders } from '../../../remote/email'
import { getConfig } from '../../../config'
import { urls } from '../urls'
import {
  RESET_STATE,
  SUBMIT_USER_PASSWORD,
  SET_USER_INFORMATIONS,
  SET_ORGANIZATION_DETAILS,
  CHECK_DOMAIN,
  CHANGE_STEP,
  SET_EMAIL_VERIFICATION_CODE,
  SKIP_EXISTING_ORG_SUGGESTIONS,
  CHECK_EMAIL_HANDLER,
  SET_ONBOARDING_TEMPLATES,
  CANCEL_SIGNUP_REMINDER,
  SET_APP_PATH,
  SET_CREATE_ORG_ERROR,
} from './actions'
import { CreateSteps } from './CreateSteps'
import { getReferrer, getUserFirstAttribution } from 'utils'

const INITIAL_STATE = {
  step: CreateSteps.Initialize,
  successRedirectPath: null,

  user: {
    email: null,
    displayName: null,
    password: null,
    photoURL: null,
    referrer: getReferrer(),
    firstAttribution: getUserFirstAttribution(),
    acceptedPrivacyPolicy: false,
    cacheKey: null,
    emailHandler: null,
  },

  joinableOrganizations: [],

  emailVerificationCode: null,

  organization: {
    name: null,
    domain: null,
    photoURL: null,
    authDomains: null,
    size: null,
    goal: null,
    remoteStance: null,
    marketingAttribution: null,
    membersCanAutoJoinByDomain: false,
    isPopularDomain: false,
  },

  templateChannelToken: null,

  authTokens: null,

  isCheckingDomain: false,
  domainCheckError: false,
  domainIsAvailable: false,

  isCreating: false,
  creationError: false,
  creationResult: null,
}

// selectors
export const getCreationState = (state) => state.organization.create
export const getStep = createSelector(getCreationState, ({ step }) => step)
export const getUser = createSelector(getCreationState, ({ user }) => user)
export const getJoinableOrganizations = createSelector(
  getCreationState,
  ({ joinableOrganizations }) => joinableOrganizations
)
export const getEmailVerificationCode = createSelector(
  getCreationState,
  ({ emailVerificationCode }) => emailVerificationCode
)
export const getOrganization = createSelector(
  getCreationState,
  ({ organization }) => organization
)
export const getTemplateChannelToken = createSelector(
  getCreationState,
  ({ templateChannelToken }) => templateChannelToken
)
export const getOrganizationAppPath = createSelector(
  getConfig,
  getOrganization,
  (config, { domain }) => urls.getOrganizationAppPath(config, domain)
)

// action creators
export function cancelSignupReminder(email) {
  return (dispatch, getState, config) => {
    dispatch({
      type: CANCEL_SIGNUP_REMINDER,
      promise: cancelSignupReminders(config, email),
      payload: email,
    })
  }
}

export function submitUserPassword(password) {
  return {
    type: SUBMIT_USER_PASSWORD,
    payload: { password },
  }
}

export function setCreateOrgError(error) {
  return {
    type: SET_CREATE_ORG_ERROR,
    payload: error,
  }
}

export function setEmailVerificationCode(emailVerificationCode) {
  return {
    type: SET_EMAIL_VERIFICATION_CODE,
    payload: emailVerificationCode,
  }
}

export function skipExistingOrgsSuggestions() {
  return {
    type: SKIP_EXISTING_ORG_SUGGESTIONS,
  }
}

export function setUserInformations(userInformations, changeStep = true) {
  return {
    type: SET_USER_INFORMATIONS,
    payload: {
      userInformations,
      changeStep,
    },
  }
}

export function setOnboardingTemplates(templatesInformations) {
  return {
    type: SET_ONBOARDING_TEMPLATES,
    payload: {
      ...templatesInformations,
    },
  }
}

const isDomainAvailableDebounced = AwesomeDebouncePromise(
  isDomainAvailable,
  300
)
export function checkDomainAvailability(domain) {
  return (dispatch, getState, config) => {
    dispatch({
      type: CHECK_DOMAIN,
      payload: domain,
      promise: isDomainAvailableDebounced(config, domain),
    })
  }
}

export function fetchEmailHandler(email) {
  return (dispatch, _, config) => {
    dispatch({
      type: CHECK_EMAIL_HANDLER,
      payload: email,
      promise: getEmailHandler(config, { email }),
      meta: {
        onSuccess: ({ handler }) =>
          dispatch(setUserInformations({ emailHandler: handler }, false)),
      },
    })
  }
}

export function setOrganizationDetails(organization, changeStep = true) {
  return {
    type: SET_ORGANIZATION_DETAILS,
    payload: { organization, changeStep },
  }
}

export function setStep(step) {
  return { type: CHANGE_STEP, payload: { step } }
}

export function goToSignup() {
  return setStep(CreateSteps.Start)
}

export function goToEmailVerification() {
  return setStep(CreateSteps.EmailVerification)
}

export function goToTeamUrlForm() {
  return setStep(CreateSteps.TeamUrlSubmitted)
}

export function setAppPath(url) {
  return { type: SET_APP_PATH, payload: url }
}

export default function reducer(state = INITIAL_STATE, action) {
  const { type, payload } = action
  switch (type) {
    case SET_EMAIL_VERIFICATION_CODE: {
      return {
        ...state,
        emailVerificationCode: payload,
      }
    }

    case SUBMIT_USER_PASSWORD: {
      return {
        ...state,
        user: {
          ...state.user,
          password: payload.password,
        },
        step: CreateSteps.Creating,
      }
    }

    case SET_USER_INFORMATIONS: {
      const { changeStep, userInformations } = payload
      return {
        ...state,
        step: changeStep ? CreateSteps.InformationsSubmitted : state.step,
        user: {
          ...state.user,
          ...userInformations,
        },
      }
    }

    case SET_ONBOARDING_TEMPLATES: {
      const { templateChannelToken } = payload
      return {
        ...state,
        templateChannelToken,
      }
    }

    case CHECK_DOMAIN:
      return handle(state, action, {
        start: (s) => ({
          ...s,
          isCheckingDomain: true,
          domainCheckError: false,
          domainIsAvailable: false,
        }),
        failure: (s) => ({
          ...s,
          domainCheckError: payload,
        }),
        success: (s) => ({
          ...s,
          domainCheckError: false,
          domainIsAvailable: payload,
        }),
        finish: (s) => ({
          ...s,
          isCheckingDomain: false,
        }),
      })

    case SET_APP_PATH: {
      return { ...state, successRedirectPath: payload }
    }

    case SET_ORGANIZATION_DETAILS: {
      const { organization, changeStep } = payload

      return {
        ...state,
        step: changeStep ? CreateSteps.InformationsSubmitted : state.step,
        organization: {
          ...state.organization,
          ...organization,
        },
      }
    }

    case CHANGE_STEP: {
      const { step } = payload
      return { ...state, step }
    }

    case SET_CREATE_ORG_ERROR: {
      return { ...state, creationError: action.payload }
    }

    case RESET_STATE: {
      return INITIAL_STATE
    }

    default:
      return state
  }
}
